import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from 'axios';
import { config } from '../../../config';
import {
  Save,
  X,
  BookOpen,
  FolderOpen,
  FileText,
  Sigma,
  Divide,
  Square,
  Superscript,
  Subscript
} from 'lucide-react';

const apiUrl = config.apiUrl;

const UpdateFlash = ({ formData, setFormData, subjects, onUpdate, onCancel }) => {
  const [modules, setModules] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingModules, setLoadingModules] = useState(false);
  const [loadingLessons, setLoadingLessons] = useState(false);
  const frontQuillRef = useRef(null);
  const backQuillRef = useRef(null);

  // React Quill modules configuration
  const quillModules = {
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'font': [] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'script': 'sub'}, { 'script': 'super' }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'align': [] }],
        ['blockquote', 'code-block'],
        ['link', 'image'],
        ['clean']
      ]
    },
    clipboard: {
      matchVisual: false,
    }
  };

  const quillFormats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'script',
    'list', 'bullet', 'indent',
    'align',
    'blockquote', 'code-block',
    'link', 'image'
  ];

  // Insert math symbol at cursor position
  const insertMathSymbol = (symbol, editorType) => {
    let quill;
    if (editorType === 'front' && frontQuillRef.current) {
      quill = frontQuillRef.current.getEditor();
    } else if (editorType === 'back' && backQuillRef.current) {
      quill = backQuillRef.current.getEditor();
    }
    
    if (quill) {
      const range = quill.getSelection();
      if (range) {
        quill.insertText(range.index, symbol);
        quill.setSelection(range.index + symbol.length);
      } else {
        quill.insertText(quill.getLength(), symbol);
      }
    }
  };

  // Custom toolbar component for math symbols
  const MathToolbar = ({ editorType }) => (
    <div className="flex flex-wrap items-center gap-1 p-2 border-b border-gray-200 bg-gray-50 mb-4">
      <span className="font-medium text-sm text-gray-700 mr-2">গাণিতিক চিহ্ন:</span>
      
      <button 
        className="p-1 hover:bg-gray-200 rounded flex items-center text-xs" 
        title="ভগ্নাংশ যোগ করুন"
        onClick={() => insertMathSymbol('\\frac{a}{b}', editorType)}
      >
        <Divide className="h-3 w-3 mr-1" />
        ভগ্নাংশ
      </button>
      
      <button 
        className="p-1 hover:bg-gray-200 rounded flex items-center text-xs" 
        title="গুণ করুন"
        onClick={() => insertMathSymbol('\\times', editorType)}
      >
        <span className="text-sm font-bold mr-1">×</span>
        গুণ
      </button>
      
      <button 
        className="p-1 hover:bg-gray-200 rounded flex items-center text-xs" 
        title="বর্গমূল যোগ করুন"
        onClick={() => insertMathSymbol('\\sqrt{x}', editorType)}
      >
        <Square className="h-3 w-3 mr-1" />
        বর্গমূল
      </button>
      
      <button 
        className="p-1 hover:bg-gray-200 rounded flex items-center text-xs" 
        title="সুপারスクリプ্ট যোগ করুন"
        onClick={() => insertMathSymbol('x^{2}', editorType)}
      >
        <Superscript className="h-3 w-3 mr-1" />
        সুপারスクリプ্ট
      </button>
      
      <button 
        className="p-1 hover:bg-gray-200 rounded flex items-center text-xs" 
        title="সাবスクリプ্ট যোগ করুন"
        onClick={() => insertMathSymbol('x_{2}', editorType)}
      >
        <Subscript className="h-3 w-3 mr-1" />
        সাবスクリプ্ট
      </button>
      
      <button 
        className="p-1 hover:bg-gray-200 rounded flex items-center text-xs" 
        title="সমষ্টি যোগ করুন"
        onClick={() => insertMathSymbol('\\sum_{i=1}^{n}', editorType)}
      >
        <Sigma className="h-3 w-3 mr-1" />
        সমষ্টি
      </button>
      
      <button 
        className="p-1 hover:bg-gray-200 rounded flex items-center text-xs" 
        title="ইন্টিগ্রাল যোগ করুন"
        onClick={() => insertMathSymbol('\\int_{a}^{b} f(x) dx', editorType)}
      >
        <span className="text-xs font-bold mr-1">∫</span>
        ইন্টিগ্রাল
      </button>
    </div>
  );

  useEffect(() => {
    if (formData.subject_id) {
      fetchModulesBySubject(formData.subject_id);
    }
  }, [formData.subject_id]);

  useEffect(() => {
    if (formData.module_id) {
      fetchLessonsByModule(formData.module_id);
    }
  }, [formData.module_id]);

  const fetchModulesBySubject = async (subjectId) => {
    setLoadingModules(true);
    try {
      const response = await axios.get(`${apiUrl}/subjects/${subjectId}/modules`);
      setModules(response.data.data || response.data);
    } catch (err) {
      console.error('Error fetching modules:', err);
    } finally {
      setLoadingModules(false);
    }
  };

  const fetchLessonsByModule = async (moduleId) => {
    setLoadingLessons(true);
    try {
      const response = await axios.get(`${apiUrl}/modules/${moduleId}/lessons`);
      setLessons(response.data.data || response.data);
    } catch (err) {
      console.error('Error fetching lessons:', err);
    } finally {
      setLoadingLessons(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFrontChange = (value) => {
    setFormData(prev => ({
      ...prev,
      front: value
    }));
  };

  const handleBackChange = (value) => {
    setFormData(prev => ({
      ...prev,
      back: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      if (!formData.id) {
        throw new Error('আপডেট করার জন্য ফ্ল্যাশকার্ড ID পাওয়া যায়নি');
      }
      
      const response = await axios.put(`${apiUrl}/flashes/${formData.id}`, formData);
      onUpdate(response.data.data);
    } catch (err) {
      console.error('Error updating flash card:', err);
      setError(err.response?.data?.message || 'फ्ल্যাশকার্ড আপডেট করতে সমস্যা হয়েছে। আবার চেষ্টা করুন।');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4 flex items-center">
          <Save className="h-5 w-5 mr-2 text-yellow-600" />
          ফ্ল্যাশকার্ড সম্পাদনা করুন
        </h2>
        
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 rounded-lg flex items-center">
            <X className="h-4 w-4 mr-2" />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Subject, Module, Lesson Selection */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <BookOpen className="h-4 w-4 mr-2 text-blue-600" />
                বিষয় *
              </label>
              <select
                name="subject_id"
                value={formData.subject_id}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">বিষয় নির্বাচন করুন</option>
                {subjects.map(subject => (
                  <option key={subject.id} value={subject.id}>
                    {subject.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <FolderOpen className="h-4 w-4 mr-2 text-blue-600" />
                মডিউল
              </label>
              <select
                name="module_id"
                value={formData.module_id}
                onChange={handleChange}
                disabled={!formData.subject_id || loadingModules}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
              >
                <option value="">মডিউল নির্বাচন করুন</option>
                {loadingModules ? (
                  <option value="">লোড হচ্ছে...</option>
                ) : (
                  modules.map(module => (
                    <option key={module.id} value={module.id}>
                      {module.name}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <FileText className="h-4 w-4 mr-2 text-blue-600" />
                লেসন
              </label>
              <select
                name="lesson_id"
                value={formData.lesson_id}
                onChange={handleChange}
                disabled={!formData.module_id || loadingLessons}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
              >
                <option value="">লেসন নির্বাচন করুন</option>
                {loadingLessons ? (
                  <option value="">লোড হচ্ছে...</option>
                ) : (
                  lessons.map(lesson => (
                    <option key={lesson.id} value={lesson.id}>
                      {lesson.title}
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              সামনের অংশ *
            </label>
            
            {/* Math Toolbar for Front */}
            <MathToolbar editorType="front" />
            
            <ReactQuill
              ref={frontQuillRef}
              value={formData.front}
              onChange={handleFrontChange}
              modules={quillModules}
              formats={quillFormats}
              theme="snow"
              className="h-48 mb-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="ফ্ল্যাশকার্ডের সামনের অংশ লিখুন"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              পিছনের অংশ *
            </label>
            
            {/* Math Toolbar for Back */}
            <MathToolbar editorType="back" />
            
            <ReactQuill
              ref={backQuillRef}
              value={formData.back}
              onChange={handleBackChange}
              modules={quillModules}
              formats={quillFormats}
              theme="snow"
              className="h-48 mb-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="ফ্ল্যাশকার্ডের পিছনের অংশ লিখুন"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              বাতিল
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  আপডেট হচ্ছে...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  আপডেট করুন
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateFlash;